import CloseIcon from '@mui/icons-material/Close';
import {
    Autocomplete,
    AutocompleteRenderOptionState,
    Box,
    Checkbox,
    Chip,
    DialogActions,
    DialogTitle,
    FormControl,
    Icon,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import CustomDialog from 'components/custom/CustomDialog';
import { CustomButton } from 'components/roundedbutton/CustomButton';
import { useGenerateUuid } from 'hooks/useGenerateUuid';
import { CustomSwitch } from 'pages/cadastro-pessoa/CadastroPessoa';
import { useCallback, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { theme } from 'theme';
import { UsuarioTO, rowGrid } from './Configuracoes.model';

type ConfiguracaoDescontoModalProps = {
    closeAddConfiguracao: () => void;
    onConfirm: () => void;
    usuarios: Array<UsuarioTO>;
    usuariosSelecionados: Array<UsuarioTO>;
    onAddUsuario: (newArray: Array<UsuarioTO>) => void;
    onAddAll: () => void;
    onRemoveAll: () => void;
    descontoMaximoNoItem: number;
    onSetMaxDescItem: (maxDesc: number) => void;
    descontoMaximoPedido: number;
    onSetDescontoMaximoPedido: (maxDesc: number) => void;
    acrescimoMaximoPedido: number;
    onSetAcrescimoMaximoPedido: (maxDesc: number) => void;
    autorizaDesconto: boolean;
    autorizaDescontoOptions: Array<boolean>;
    onChangeAutorizaDesconto: (option: string) => void;
    rows: Array<rowGrid>;
    usuarioEdit: rowGrid | null;
    replicaOutrosEstabelecimentos: boolean;
    onToggleReplicarConfiguracaoDesconto: () => void;
};

const ConfiguracaoDescontoAcrescimoModal = (props: ConfiguracaoDescontoModalProps) => {
    const [randomUuid] = useGenerateUuid();
    const descontoMaxItemRef = useRef<HTMLInputElement | null>(null);
    const descontoMaxRodapeRef = useRef<HTMLInputElement | null>(null);
    const acrescimoMaxRodapeRef = useRef<HTMLInputElement | null>(null);

    const onFocusDescontoMaxItem = useCallback(() => {
        if (props.descontoMaximoNoItem) {
            return descontoMaxItemRef.current?.setSelectionRange(4, 4);
        }
        return descontoMaxItemRef.current?.setSelectionRange(1, 1);
    }, [props.descontoMaximoNoItem]);

    const onFocusDescontoMaxRodape = useCallback(() => {
        if (props.descontoMaximoPedido) {
            return descontoMaxRodapeRef.current?.setSelectionRange(4, 4);
        }
        return descontoMaxRodapeRef.current?.setSelectionRange(1, 1);
    }, [props.descontoMaximoPedido]);

    const onFocusAcrescimoMaxRodape = useCallback(() => {
        acrescimoMaxRodapeRef.current?.setSelectionRange(4, 4);
    }, []);

    const getSimNaoOfBoolean = useCallback((e: boolean) => {
        if (e) {
            return 'SIM';
        }
        return 'NÃO';
    }, []);

    const handleMenuItemClick = (username: string) => {
        const selectedUser = props.usuarios.find(e => e.username === username);

        if (selectedUser) {
            const updatedUsuarios =
                props.usuariosSelecionados.includes(selectedUser) ?
                    props.usuariosSelecionados.filter(e => e.username !== username)
                :   [...props.usuariosSelecionados, selectedUser];

            props.onAddUsuario(updatedUsuarios);
        }
    };

    const renderOptions = (option: UsuarioTO, state: AutocompleteRenderOptionState) => {
        return (
            <MenuItem
                onClick={handleMenuItemClick.bind(null, option.username)}
                key={randomUuid()}
                value={option.username}>
                <Checkbox
                    id={`item-${option.username?.toLowerCase()}-input`}
                    sx={{
                        '&.MuiCheckbox-root.Mui-checked': {
                            color: theme.palette.primary.main,
                        },
                    }}
                    checked={props.usuariosSelecionados.some(e => e.username === option.username)}
                />
                <ListItemText primary={option.username} />
            </MenuItem>
        );
    };

    const getValue = useCallback(() => {
        if (props.usuarioEdit) {
            const found = props.rows.find(e => e.uuid === props.usuarioEdit?.uuid)!;
            const usuarioTO: UsuarioTO = {
                username: found.usuario,
                uuid: found.uuid!,
            };
            return [usuarioTO];
        }
        return props.usuariosSelecionados;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.usuariosSelecionados]);

    const getOptions = useCallback(() => {
        if (props.usuarioEdit) {
            return [];
        }
        return props.usuarios.filter(e => !props.rows.some(el => e.username === el.usuario));
        //eslint-disable-next-line
    }, [props.usuarios, props.usuarioEdit]);

    return (
        <CustomDialog
            open={true}
            fullWidth
            onClose={props.closeAddConfiguracao}>
            <DialogTitle
                sx={{ border: 0, margin: 0, padding: 0 }}
                align='left'>
                Configuração de desconto e acréscimo
            </DialogTitle>
            <FormControl style={{ marginTop: '40px' }}>
                <Autocomplete
                    sx={{ maxHeight: '200px', overflowX: 'hidden', overflowY: 'scroll' }}
                    multiple
                    filterSelectedOptions
                    value={getValue()}
                    options={getOptions()}
                    getOptionLabel={option => option.username}
                    onInputChange={e => {
                        if (e?.type === 'click') {
                            props.onRemoveAll();
                        }
                    }}
                    noOptionsText='Nenhum valor encontrado'
                    renderInput={params => (
                        <TextField
                            {...params}
                            variant='standard'
                            label={props.usuarioEdit ? 'Usuário' : 'Usuários'}
                        />
                    )}
                    renderOption={(_props, option, state, _ownerState) =>
                        renderOptions(option, state)
                    }
                    renderTags={p =>
                        p.map(e => {
                            return (
                                <Box
                                    key={e.uuid}
                                    sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.background.default,
                                        borderRadius: '50px',
                                        marginRight: '5px',
                                        marginTop: '2px',
                                        marginBottom: '2px',
                                    }}>
                                    <Chip
                                        key={e.username}
                                        sx={{
                                            height: '1.5rem',
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.background.default,
                                        }}
                                        label={e.username}
                                    />
                                    {!props.usuarioEdit && (
                                        <IconButton
                                            onClick={handleMenuItemClick.bind(null, e.username)}
                                            sx={{
                                                color: theme.palette.background.default,
                                                padding: 0,
                                                paddingRight: '2px',
                                                fontSize: '15px',
                                            }}>
                                            <CloseIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })
                    }
                />
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'space-evenly', marginTop: '30px' }}>
                <FormControl>
                    <Typography sx={{ fontSize: '12px', color: 'grey' }}>
                        Desconto máximo no item
                    </Typography>
                    <NumericFormat
                        inputRef={descontoMaxItemRef}
                        onFocus={onFocusDescontoMaxItem}
                        style={{ width: '98%' }}
                        id='desconto_maximo_item_numeric_format'
                        decimalScale={2}
                        fixedDecimalScale
                        allowedDecimalSeparators={[',']}
                        customInput={TextField}
                        decimalSeparator=','
                        suffix={' %'}
                        thousandsGroupStyle='thousand'
                        thousandSeparator='.'
                        variant='standard'
                        allowNegative={false}
                        inputProps={{
                            style: {
                                textAlign: 'left',
                                fontSize: '24px',
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                                fontFamily: 'Work Sans, sans-serif',
                            },
                        }}
                        isAllowed={({ floatValue }) => {
                            if (!floatValue) {
                                return true;
                            }
                            if (floatValue > 100) {
                                return false;
                            }
                            return true;
                        }}
                        value={props.descontoMaximoNoItem}
                        onValueChange={({ floatValue }) => props.onSetMaxDescItem(floatValue ?? 0)}
                    />
                </FormControl>

                <FormControl>
                    <Typography sx={{ fontSize: '12px', color: 'grey' }}>
                        Desconto máximo no rodapé
                    </Typography>
                    <NumericFormat
                        inputRef={descontoMaxRodapeRef}
                        onFocus={onFocusDescontoMaxRodape}
                        style={{ width: '98%' }}
                        id='desconto_maximo_rodape_numeric_format'
                        decimalScale={2}
                        fixedDecimalScale
                        allowedDecimalSeparators={[',']}
                        customInput={TextField}
                        decimalSeparator=','
                        suffix={' %'}
                        thousandsGroupStyle='thousand'
                        thousandSeparator='.'
                        variant='standard'
                        allowNegative={false}
                        inputProps={{
                            style: {
                                textAlign: 'left',
                                fontSize: '24px',
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                                fontFamily: 'Work Sans, sans-serif',
                            },
                        }}
                        isAllowed={({ floatValue }) => {
                            if (!floatValue) {
                                return true;
                            }
                            if (floatValue > 100) {
                                return false;
                            }
                            return true;
                        }}
                        value={props.descontoMaximoPedido}
                        onValueChange={({ floatValue }) =>
                            props.onSetDescontoMaximoPedido(floatValue ?? 0)
                        }
                    />
                </FormControl>

                <FormControl>
                    <Typography sx={{ fontSize: '12px', color: 'grey' }}>
                        Acréscimo máximo no rodapé
                    </Typography>
                    <NumericFormat
                        inputRef={acrescimoMaxRodapeRef}
                        onFocus={onFocusAcrescimoMaxRodape}
                        style={{ width: '98%' }}
                        id='acrescimo_maximo_rodape_numeric_format'
                        decimalScale={2}
                        fixedDecimalScale
                        allowedDecimalSeparators={[',']}
                        customInput={TextField}
                        decimalSeparator=','
                        prefix={'R$ '}
                        thousandsGroupStyle='thousand'
                        thousandSeparator='.'
                        variant='standard'
                        allowNegative={false}
                        inputProps={{
                            style: {
                                textAlign: 'left',
                                fontSize: '24px',
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                                fontFamily: 'Work Sans, sans-serif',
                            },
                        }}
                        value={props.acrescimoMaximoPedido}
                        onValueChange={({ floatValue }) =>
                            props.onSetAcrescimoMaximoPedido(floatValue ?? 0)
                        }
                    />
                </FormControl>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-evenly', marginTop: '30px' }}>
                <Typography sx={{ fontSize: '14px' }}></Typography>
            </Box>

            <FormControl>
                <InputLabel
                    variant='standard'
                    id='autoriza_desconto_title'
                    style={{ fontSize: '16px' }}>
                    Autoriza desconto?
                </InputLabel>
                <Select
                    variant='standard'
                    inputProps={{ id: 'select_autoriza_desconto' }}
                    id='autoriza_deconto_boolean'
                    onChange={e => props.onChangeAutorizaDesconto(e.target.value)}
                    value={getSimNaoOfBoolean(props.autorizaDesconto)}>
                    {props.autorizaDescontoOptions.map((e, i) => {
                        return (
                            <MenuItem
                                value={getSimNaoOfBoolean(e)}
                                key={getSimNaoOfBoolean(e)}>
                                {getSimNaoOfBoolean(e)}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            <FormControl>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        marginTop: '20px',
                        alignItems: 'center',
                    }}>
                    <CustomSwitch
                        id='permite-parcial-input'
                        checked={props.replicaOutrosEstabelecimentos}
                        name='permite-parcial'
                        onChange={props.onToggleReplicarConfiguracaoDesconto}
                    />

                    <Typography
                        sx={{
                            fontSize: 18,
                            color: theme.palette.text.primary,
                            marginLeft: '10px',
                        }}>
                        Replicar configuração de desconto para todos os estabelecimentos vinculados
                        a esse usuário?
                    </Typography>
                </Box>
            </FormControl>

            <DialogActions
                sx={{
                    justifyContent: 'space-between',
                    marginTop: '20px',
                }}>
                <CustomButton
                    onClick={props.closeAddConfiguracao}
                    variant='contained'
                    color='error'
                    startIcon={<Icon className='fa-solid fa-xmark' />}>
                    Cancelar
                </CustomButton>
                <CustomButton
                    onClick={props.onConfirm}
                    disabled={
                        props.usuarioEdit === null ? !props.usuariosSelecionados.length : false
                    }
                    id='confirm_button_configuracao_desconto'
                    type='submit'
                    variant='contained'
                    color='success'
                    startIcon={<Icon className='fa-solid fa-check' />}>
                    Confirmar
                </CustomButton>
            </DialogActions>
        </CustomDialog>
    );
};

export default ConfiguracaoDescontoAcrescimoModal;
